import React from "react";
import { toast } from "react-hot-toast";

import { useAppSelector } from "@/hooks/redux";
import FleetService from "@/services/FleetService";

export interface VehicleFormData {
  assignedDriver: string;
  brand: string;
  color: string;
  fuel_type: string;
  insurance_number: string;
  max_load: string;
  mileage: string;
  model: string;
  plate_number: string;
  registrationDocument?: File;
  status: "active" | "maintenance" | "retired";
  type: string;
  year: string;
}

interface AddVehicleModalProps {
  onClose: () => void;
  onSubmit: (data: VehicleFormData) => void;
  open: boolean;
}

const AddVehicleModal: React.FC<AddVehicleModalProps> = ({
  onClose,
  onSubmit,
  open,
}) => {
  const { user } = useAppSelector((state) => state.auth);
  const [form, setForm] = React.useState<any>({
    assignedDriver: "",
    brand: "",
    color: "",
    fuel_type: "",
    insurance_number: "",
    make: "",
    max_load: "",
    mileage: "",
    model: "",
    notes: "",
    plate_number: "",
    registrationDocument: undefined,
    status: "active",
    type: "",
    vin: "",
    year: "",
  });
  const [submitting, setSubmitting] = React.useState(false);
  const [drivers, setDrivers] = React.useState<
    Array<{ id: string; name: string }>
  >([]);
  const [customColor, setCustomColor] = React.useState("");
  const [customFuel, setCustomFuel] = React.useState("");

  React.useEffect(() => {
    if (open && user?.id) {
      FleetService.getDrivers()
        .then((list) => {
          console.log("Raw drivers data:", list);
          // Some APIs may use 'created_by', others 'createdBy' or similar
          const filtered = list.filter(
            (d: any) => String(d.created_by) === String(user.id),
          );
          const driversList = filtered.length > 0 ? filtered : list;
          const mappedDrivers = driversList.map((d: any) => ({
            id: d.id, // Use drivers.id
            name:
              d.name ||
              d.full_name ||
              d.first_name ||
              d.driver_name ||
              `Driver ${d.id}`,
          }));
          console.log("Mapped drivers:", mappedDrivers);
          setDrivers(mappedDrivers);
        })
        .catch((err) => {
          console.error("Error fetching drivers:", err);
          setDrivers([]);
        });
    }
  }, [open, user]);

  if (!open) return null;

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setForm({ ...form, registrationDocument: e.target.files[0] });
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className="w-full max-w-md rounded-lg bg-white p-6 shadow-lg dark:bg-gray-900 md:max-w-3xl lg:max-w-4xl xl:max-w-5xl 2xl:max-w-6xl"
        onClick={(e) => e.stopPropagation()}
        style={{ maxHeight: "90vh", overflowY: "auto", width: "80vw" }}
      >
        <h2 className="mb-4 text-lg font-semibold text-gray-900 dark:text-gray-100">
          Add Vehicle
        </h2>
        <form
          className="grid grid-cols-1 gap-x-6 gap-y-4 md:grid-cols-2"
          onSubmit={async (e) => {
            e.preventDefault();
            setSubmitting(true);
            let errorMsg = "";
            // Validation: year must not be greater than 2026
            if (parseInt(form.year) > 2026) {
              errorMsg = "Year must not be greater than 2026.";
            }
            // Validation: plate required
            if (!form.plate) {
              errorMsg = errorMsg ? errorMsg + "\n" : "";
              errorMsg += "Plate number is required.";
            }
            // Validation: registration document required
            if (!form.registrationDocument) {
              errorMsg = errorMsg ? errorMsg + "\n" : "";
              errorMsg += "Registration document is required.";
            }
            // Validation: mileage required and must be a positive integer
            if (!form.mileage || form.mileage.trim() === "") {
              errorMsg = errorMsg ? errorMsg + "\n" : "";
              errorMsg += "Mileage is required.";
            } else if (
              isNaN(Number(form.mileage)) ||
              !Number.isInteger(Number(form.mileage)) ||
              Number(form.mileage) < 0
            ) {
              errorMsg = errorMsg ? errorMsg + "\n" : "";
              errorMsg += "Mileage must be a positive integer.";
            }
            if (errorMsg) {
              toast.error(errorMsg);
              setSubmitting(false);
              return;
            }
            try {
              // Prepare data for API
              const payload: any = {
                assigned_driver_id:
                  form.assignedDriver !== "" ? form.assignedDriver : null,
                brand: form.brand || "",
                color: form.color || "",
                fuel_type: form.fuelType || "",
                insurance_number: form.insuranceNumber || "",
                make: form.make || "",
                max_load: form.maxLoad || "",
                mileage: form.mileage || "",
                model: form.model || "",
                notes: form.notes || "",
                plate_number: form.plate || "",
                status: form.status || "active",
                type: form.type || "",
                vin: form.vin || "",
                year: form.year || new Date().getFullYear().toString(),
              };
              console.log("Submitting vehicle payload:", payload);
              if (form.registrationDocument) {
                const fd = new FormData();
                Object.entries(payload).forEach(([key, value]) => {
                  if (value !== undefined && value !== null && value !== "") {
                    fd.append(key, value as string);
                  }
                });
                fd.append("registration_document", form.registrationDocument);
                console.log("Submitting with FormData");
                console.log("File details:", {
                  name: form.registrationDocument.name,
                  size: form.registrationDocument.size,
                  type: form.registrationDocument.type,
                });
                // Log FormData entries
                for (const pair of fd.entries()) {
                  console.log(
                    pair[0] +
                      ": " +
                      (pair[1] instanceof File
                        ? `File: ${pair[1].name}`
                        : pair[1]),
                  );
                }
                await FleetService.createVehicle(fd as any);
              } else {
                console.log("Submitting without file");
                await FleetService.createVehicle(payload);
              }
              console.log("Vehicle created successfully");
              toast.success("Vehicle added successfully!");
              setTimeout(() => {
                onSubmit(form);
                setForm({
                  assignedDriver: "",
                  brand: "",
                  color: "",
                  fuelType: "",
                  insuranceNumber: "",
                  maxLoad: "",
                  mileage: "",
                  model: "",
                  plate: "",
                  registrationDocument: undefined,
                  status: "active",
                  type: "",
                  year: "",
                });
                onClose();
              }, 400);
            } catch (error: any) {
              console.error("Failed to create vehicle:", error);
              if (error.response) {
                const data = error.response.data;
                let errorMsg = data.message || "Failed to add vehicle.";
                if (data.errors) {
                  if (data.errors.registration_document) {
                    errorMsg = Array.isArray(data.errors.registration_document)
                      ? data.errors.registration_document[0]
                      : data.errors.registration_document;
                  } else {
                    // Show the first error message for any other field
                    const firstField = Object.keys(data.errors)[0];
                    const firstError = data.errors[firstField];
                    errorMsg = Array.isArray(firstError)
                      ? firstError[0]
                      : firstError;
                  }
                }
                toast.error(errorMsg);
              } else if (error.message) {
                toast.error(`Error: ${error.message}`);
              } else {
                toast.error("Unknown error occurred.");
              }
            } finally {
              setSubmitting(false);
            }
          }}
        >
          {/* Brand */}
          <InputField
            className="w-full"
            id="vehicle-brand"
            label="Brand"
            onChange={(value) => setForm({ ...form, brand: value })}
            value={form.brand}
          />
          {/* Model */}
          <InputField
            className="w-full"
            id="vehicle-model"
            label="Model"
            onChange={(value) => setForm({ ...form, model: value })}
            value={form.model}
          />
          {/* Year (select) */}
          <div className="relative w-full">
            <select
              className="peer w-full rounded border border-gray-300 bg-transparent px-3 py-2 text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100"
              id="vehicle-year"
              onChange={(e) => setForm({ ...form, year: e.target.value })}
              required
              value={form.year}
            >
              <option value="">Select Year</option>
              {Array.from({ length: 2026 - 1990 + 1 }, (_, i) => 1990 + i).map(
                (year) => (
                  <option key={year} value={year.toString()}>
                    {year}
                  </option>
                ),
              )}
            </select>
            <label
              className="pointer-events-none absolute left-3 top-2 z-10 origin-[0] -translate-y-4 scale-75 transform bg-white px-1 text-gray-500 duration-200 peer-focus:-translate-y-4 peer-focus:scale-75 dark:bg-gray-800 dark:text-gray-400"
              htmlFor="vehicle-year"
            >
              Year
            </label>
          </div>
          {/* Type */}
          <div className="relative w-full">
            <select
              className="peer w-full rounded border border-gray-300 bg-transparent px-3 py-2 text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100"
              id="vehicle-type"
              onChange={(e) => setForm({ ...form, type: e.target.value })}
              required
              value={form.type}
            >
              <option value="">Select Type</option>
              <option value="Bike">Bike</option>
              <option value="Motorcycle">Motorcycle</option>
              <option value="Sedan">Sedan</option>
              <option value="Hatchback">Hatchback</option>
              <option value="SUV">SUV</option>
              <option value="Mini Van">Mini Van</option>
              <option value="Cargo Van">Cargo Van</option>
              <option value="Service Van">Service Van</option>
              <option value="Pickup">Pickup</option>
              <option value="Light Truck">Light Truck</option>
              <option value="Utility Truck">Utility Truck</option>
              <option value="Minibus">Minibus</option>
              <option value="Bus">Bus</option>
              <option value="Luxury Car">Luxury Car</option>
            </select>
            <label
              className="pointer-events-none absolute left-3 top-2 z-10 origin-[0] -translate-y-4 scale-75 transform bg-white px-1 text-gray-500 duration-200 peer-focus:-translate-y-4 peer-focus:scale-75 dark:bg-gray-800 dark:text-gray-400"
              htmlFor="vehicle-type"
            >
              Type
            </label>
          </div>
          {/* Color (select + custom) */}
          <div className="relative w-full">
            <select
              className="peer w-full rounded border border-gray-300 bg-transparent px-3 py-2 text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100"
              id="vehicle-color"
              onChange={(e) => {
                setForm({ ...form, color: e.target.value });
                if (e.target.value === "Other") setCustomColor("");
              }}
              required
              value={form.color}
            >
              <option value="">Select Color</option>
              <option value="White">White</option>
              <option value="Black">Black</option>
              <option value="Silver">Silver</option>
              <option value="Blue">Blue</option>
              <option value="Red">Red</option>
              <option value="Gray">Gray</option>
              <option value="Green">Green</option>
              <option value="Yellow">Yellow</option>
              <option value="Other">Other</option>
            </select>
            <label
              className="pointer-events-none absolute left-3 top-2 z-10 origin-[0] -translate-y-4 scale-75 transform bg-white px-1 text-gray-500 duration-200 peer-focus:-translate-y-4 peer-focus:scale-75 dark:bg-gray-800 dark:text-gray-400"
              htmlFor="vehicle-color"
            >
              Color
            </label>
            {form.color === "Other" && (
              <input
                className="mt-2 w-full rounded border border-gray-300 px-3 py-2 text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100"
                onChange={(e) => {
                  setCustomColor(e.target.value);
                  setForm({ ...form, color: e.target.value });
                }}
                placeholder="Enter custom color"
                required
                value={customColor}
              />
            )}
          </div>
          {/* Plate */}
          <InputField
            className="w-full"
            id="vehicle-plate"
            label="Plate Number"
            onChange={(value) => setForm({ ...form, plate: value })}
            value={form.plate}
          />
          {/* Fuel Type (select + custom) */}
          <div className="relative w-full">
            <select
              className="peer w-full rounded border border-gray-300 bg-transparent px-3 py-2 text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100"
              id="vehicle-fuel"
              onChange={(e) => {
                setForm({ ...form, fuelType: e.target.value });
                if (e.target.value === "Other") setCustomFuel("");
              }}
              required
              value={form.fuelType}
            >
              <option value="">Select Fuel Type</option>
              <option value="Petrol">Petrol</option>
              <option value="Diesel">Diesel</option>
              <option value="Electric">Electric</option>
              <option value="Hybrid">Hybrid</option>
              <option value="Other">Other</option>
            </select>
            <label
              className="pointer-events-none absolute left-3 top-2 z-10 origin-[0] -translate-y-4 scale-75 transform bg-white px-1 text-gray-500 duration-200 peer-focus:-translate-y-4 peer-focus:scale-75 dark:bg-gray-800 dark:text-gray-400"
              htmlFor="vehicle-fuel"
            >
              Fuel Type
            </label>
            {form.fuelType === "Other" && (
              <input
                className="mt-2 w-full rounded border border-gray-300 px-3 py-2 text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100"
                onChange={(e) => {
                  setCustomFuel(e.target.value);
                  setForm({ ...form, fuelType: e.target.value });
                }}
                placeholder="Enter custom fuel type"
                required
                value={customFuel}
              />
            )}
          </div>
          {/* Max Load */}
          <InputField
            className="w-full"
            id="vehicle-maxload"
            label="Max Load (kg)"
            onChange={(value) => setForm({ ...form, maxLoad: value })}
            type="number"
            value={form.maxLoad}
          />
          {/* Mileage */}
          <InputField
            className="w-full"
            id="vehicle-mileage"
            label="Mileage (km)"
            onChange={(value) => setForm({ ...form, mileage: value })}
            type="number"
            value={form.mileage || ""}
          />
          {/* Assigned Driver (select) */}
          <div className="relative w-full">
            <select
              className="peer w-full rounded border border-gray-300 bg-white px-3 py-2 text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100"
              id="vehicle-driver"
              onChange={(e) =>
                setForm({ ...form, assignedDriver: e.target.value })
              }
              value={form.assignedDriver}
            >
              <option
                className="bg-white text-gray-900 dark:bg-gray-800 dark:text-gray-100"
                value=""
              >
                Select Driver (Optional)
              </option>
              {drivers.map((driver) => (
                <option
                  className="bg-white text-gray-900 dark:bg-gray-800 dark:text-gray-100"
                  key={driver.id}
                  value={driver.id}
                >
                  {driver.name}
                </option>
              ))}
            </select>
            <label
              className="pointer-events-none absolute left-3 top-2 z-10 origin-[0] -translate-y-4 scale-75 transform bg-white px-1 text-gray-500 duration-200 peer-focus:-translate-y-4 peer-focus:scale-75 dark:bg-gray-800 dark:text-gray-400"
              htmlFor="vehicle-driver"
            >
              Assigned Driver
            </label>
          </div>
          {/* Insurance Number */}
          <InputField
            className="w-full"
            id="vehicle-insurance"
            label="Insurance Number"
            onChange={(value) => setForm({ ...form, insuranceNumber: value })}
            value={form.insuranceNumber}
          />
          {/* Registration Document */}
          <div className="md:col-span-2">
            <label className="mb-1 block text-gray-700 dark:text-gray-300">
              Registration Document
            </label>
            <input
              className="w-full text-gray-900 dark:text-gray-100"
              onChange={handleFileChange}
              type="file"
            />
          </div>
          {/* Status */}
          <div className="relative md:col-span-2">
            <select
              className="peer w-full rounded border border-gray-300 bg-transparent px-3 py-2 text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100"
              id="vehicle-status"
              onChange={(e) =>
                setForm({
                  ...form,
                  status: e.target.value as VehicleFormData["status"],
                })
              }
              required
              value={form.status}
            >
              <option value="active">Active</option>
              <option value="maintenance">Maintenance</option>
              <option value="retired">Retired</option>
            </select>
            <label
              className="pointer-events-none absolute left-3 top-2 z-10 origin-[0] -translate-y-4 scale-75 transform bg-white px-1 text-gray-500 duration-200 peer-focus:-translate-y-4 peer-focus:scale-75 dark:bg-gray-800 dark:text-gray-400"
              htmlFor="vehicle-status"
            >
              Status
            </label>
          </div>

          <div className="mt-4 flex justify-end gap-2 md:col-span-2">
            <button
              className="rounded bg-gray-200 px-4 py-2 text-gray-700 dark:bg-gray-700 dark:text-gray-200"
              disabled={submitting}
              onClick={onClose}
              type="button"
            >
              Cancel
            </button>
            <button
              className="rounded bg-blue-600 px-4 py-2 text-white dark:bg-blue-500"
              disabled={submitting}
              type="submit"
            >
              {submitting ? "Adding..." : "Add"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Reusable input field component
interface InputFieldProps {
  className?: string;
  id: string;
  label: string;
  onChange: (value: string) => void;
  type?: string;
  value: string;
}

const InputField = ({
  className,
  id,
  label,
  onChange,
  type = "text",
  value,
}: InputFieldProps) => (
  <div className={`relative ${className || ""}`}>
    <input
      className="peer w-full rounded border border-gray-300 bg-transparent px-3 py-2 text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100"
      id={id}
      onChange={(e) => onChange(e.target.value)}
      required
      type={type}
      value={value}
    />
    <label
      className="pointer-events-none absolute left-3 top-2 z-10 origin-[0] -translate-y-4 scale-75 transform bg-white px-1 text-gray-500 duration-200 peer-placeholder-shown:translate-y-0 peer-placeholder-shown:scale-100 peer-focus:-translate-y-4 peer-focus:scale-75 dark:bg-gray-800 dark:text-gray-400"
      htmlFor={id}
    >
      {label}
    </label>
  </div>
);

export default AddVehicleModal;
